import React, { FC, useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { GreenButton } from '~Styles';
import { useButton, useLogger, useI18n } from '~Hooks';
import { useEntryFinder } from '~Components/EntryFinder';
import { CompleteBongQuery_venue_company_categories_items as Item } from '~Scenes/POS/Queries/__queries__';
import ItemButton from './ItemButton';
import { ItemCategoryMap } from '~Scenes/POS/categories';
import { useCart } from '~Components/CartProvider';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StageHeader = styled(Box)`
  background-color: var(--colorBlueGray1300);
  padding: 8px 12px;
  margin: 5px;
  border-radius: 4px;
`;

const OfferTitle = styled.p`
  color: var(--colorUIOrange);
  font-size: 12px;
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const ItemsGrid = styled.div`
  flex: 1;
  margin: 5px;
  line-height: 0px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 60px;
  grid-gap: 6px;
  overflow-y: auto;
  padding-bottom: 80px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

interface StageItem {
  id: string;
  title: string;
}

interface Stage {
  id: string;
  title: {
    se?: string;
    en?: string;
    es?: string;
    no?: string;
  };
  items: StageItem[];
}

interface CategoryItem {
  id: string;
  itemId: string;
  title: string;
  color: string | undefined;
  descriptionLang: any;
  manualPrice: boolean;
  volumeSuffix: string | null | undefined;
}

interface Props {
  offerName: string;
  stages: Stage[];
  categoryItems: CategoryItem[];
  allItems: Item[];
  venueId: string;
  itemToCategoryMap: ItemCategoryMap;
  onAddItem: (item: Item, manualPrice?: number, volume?: number, message?: string) => void;
  onComplete: () => void;
}

const StagedBundleGrid: FC<Props> = ({
  offerName,
  stages,
  categoryItems,
  allItems,
  venueId,
  itemToCategoryMap,
  onAddItem,
  onComplete,
}) => {
  const logger = useLogger('staged-bundle-grid-handy');
  const { i18n } = useI18n();
  const findEntryFromItem = useEntryFinder();
  const cart = useCart();

  const [currentStageIndex, setCurrentStageIndex] = useState(0);

  const currentStage = stages[currentStageIndex];
  const isLastStage = currentStageIndex === stages.length - 1;

  // Sort items by category order (descending), then alphabetically by title within each category
  const sortedItems = useMemo(() => {
    if (!currentStage?.items) return [];

    return [...currentStage.items]
      .filter((item) => {
        // Filter out items that don't exist in the menu
        const entry = findEntryFromItem(item.id);
        if (!entry) {
          logger.warn(`Item ${item.id} not found in menu, filtering out from stage`);
          return false;
        }
        return true;
      })
      .map((item) => {
        const foundItem = allItems.find((i) => i.id === item.id);
        const categoryItem = categoryItems.find((ci) => ci.itemId === item.id);
        const category = itemToCategoryMap[item.id];

        return {
          itemId: item.id,
          title: foundItem ? i18n(foundItem.titleLang) || item.title : item.title,
          color: categoryItem?.color || '#fff',
          descriptionLang: categoryItem?.descriptionLang,
          manualPrice: categoryItem?.manualPrice || false,
          volumeSuffix: categoryItem?.volumeSuffix || null,
          categoryId: categoryItem?.id || item.id,
          categoryOrder: category?.order ?? 0, // Use 0 as default if category not found
          fullItem: foundItem,
        };
      })
      .sort((a, b) => {
        // First sort by category order (descending - higher order first)
        const orderDiff = b.categoryOrder - a.categoryOrder;
        if (orderDiff !== 0) return orderDiff;

        // Then sort alphabetically by title within the same category
        return a.title.localeCompare(b.title);
      });
  }, [currentStage, findEntryFromItem, allItems, categoryItems, i18n, itemToCategoryMap]);

  // Get translated stage title
  const stageTitle = useMemo(() => {
    if (!currentStage?.title) return `Stage ${currentStageIndex + 1}`;
    return i18n(currentStage.title) || `Stage ${currentStageIndex + 1}`;
  }, [currentStage, currentStageIndex, i18n]);

  // Add items to cart without auto-advancing
  const handleAddItem = useCallback(
    (item: Item, manualPrice?: number, volume?: number, message?: string) => {
      logger.info('Item added in staged bundle', {
        itemId: item.id,
        stageIndex: currentStageIndex,
      });

      // Add item to cart (no auto-advance)
      onAddItem(item, manualPrice, volume, message);
    },
    [onAddItem, currentStageIndex, logger],
  );

  const nextButton = useButton({
    onClick: () => {
      if (isLastStage) {
        onComplete();
      } else {
        setCurrentStageIndex(currentStageIndex + 1);
        logger.info('Manually advancing to next stage', { nextStageIndex: currentStageIndex + 1 });
      }
    },
    disabled: false,
  });

  return (
    <>
      {/* Stage Header with Next/Finish button */}
      <StageHeader>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap="small">
          <Box>
            <OfferTitle>{offerName}</OfferTitle>
            <Heading level={4} color="var(--colorWhite)">
              {stageTitle}
            </Heading>
            <Paragraph level={4} color="var(--colorBlueGray200)">
              {i18n('POS.Stage')} {currentStageIndex + 1} / {stages.length}
            </Paragraph>
          </Box>

          <GreenButton {...nextButton}>
            {isLastStage ? i18n('POS.Finish') : i18n('POS.NextStage')}
          </GreenButton>
        </Box>
      </StageHeader>

      {/* Items Grid */}
      <ItemsGrid>
        {sortedItems.map((item) => {
          if (!item.fullItem) return null;

          // Calculate how many of this item are in the cart
          const itemCount = cart.items.filter((cartItem) => cartItem.itemId === item.itemId).length;

          return (
            <ItemButton
              key={item.itemId}
              button={{
                nodeId: item.itemId,
                color: item.color,
                label: item.title,
                type: 'ITEM',
                x: null,
                y: null,
                id: item.itemId,
              }}
              onPress={handleAddItem}
              onPageClicked={() => {}}
              count={itemCount}
            />
          );
        })}
      </ItemsGrid>
    </>
  );
};

export default StagedBundleGrid;
